"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"

const navLinks = [
  { id: "home", label: "home", href: "/" },
  { id: "about", label: "about", href: "/about" },
  { id: "templates", label: "templates", href: "/templates" },
  { id: "consult", label: "consult", href: "/consultation" },
  { id: "contact", label: "contact", href: "/contact" },
]

export function ResponsiveHeader() {
  const pathname = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [mobileMenuOpen])

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/"
    return pathname?.startsWith(href)
  }

  return (
    <>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "var(--bg)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div
          className="header-padding"
          style={{
            maxWidth: 1240,
            margin: "0 auto",
            padding: "0 48px",
            height: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Logo */}
          <Link
            href="/"
            style={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              flexShrink: 0,
            }}
          >
            <img
              src="/images/hsve-logo.png"
              alt="HSVE Consultation Services"
              style={{ height: 36, width: "auto" }}
            />
          </Link>

          {/* Desktop Nav */}
          <nav
            className="font-mono hide-mobile"
            style={{
              display: "flex",
              gap: 24,
              fontSize: 12,
            }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                style={{
                  color: isActive(link.href) ? "var(--accent)" : "var(--text-2)",
                  textDecoration: "none",
                }}
              >
                <span style={{ color: "var(--muted)" }}>~/</span>
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Right actions */}
          <div
            className="font-mono hide-mobile"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 20,
              fontSize: 12,
            }}
          >
            <a
              href="https://status.hsve.cc"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "var(--text-2)",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <span className="status-dot" />
              status
            </a>
            <a
              href="https://discord.gg/kDs4vSUXcv"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "var(--text-2)",
                textDecoration: "none",
              }}
            >
              discord ↗
            </a>
            <Link
              href="/login"
              style={{
                color: "var(--text-2)",
                textDecoration: "none",
              }}
            >
              sign_in()
            </Link>
            <Link
              href="/consultation"
              style={{
                color: "var(--accent-ink)",
                background: "var(--accent)",
                padding: "8px 14px",
                borderRadius: 4,
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              ./book_consultation.sh
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`hamburger-btn hide-desktop ${mobileMenuOpen ? "open" : ""}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      <div
        className={`mobile-nav-overlay ${mobileMenuOpen ? "open" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile drawer */}
      <div className={`mobile-nav-drawer ${mobileMenuOpen ? "open" : ""}`}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Link href="/" onClick={() => setMobileMenuOpen(false)}>
              <img
                src="/images/hsve-logo.png"
                alt="HSVE"
                style={{ height: 32, width: "auto" }}
              />
            </Link>
            <button
              className="hamburger-btn open"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>

        {/* Mobile nav links */}
        <nav
          className="font-mono"
          style={{
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          {navLinks.map((link) => (
            <Link
              key={link.id}
              href={link.href}
              onClick={() => setMobileMenuOpen(false)}
              style={{
                color: isActive(link.href) ? "var(--accent)" : "var(--text-2)",
                textDecoration: "none",
                padding: "14px 0",
                borderBottom: "1px solid var(--border-soft)",
                fontSize: 14,
              }}
            >
              <span style={{ color: "var(--muted)" }}>~/</span>
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Mobile actions */}
        <div
          className="font-mono"
          style={{
            padding: "8px 24px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
            fontSize: 13,
          }}
        >
          <a
            href="https://status.hsve.cc"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "var(--text-2)",
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 0",
            }}
          >
            <span className="status-dot" />
            status
          </a>
          <a
            href="https://discord.gg/kDs4vSUXcv"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "var(--text-2)",
              textDecoration: "none",
              padding: "10px 0",
            }}
          >
            discord ↗
          </a>
          <Link
            href="/login"
            onClick={() => setMobileMenuOpen(false)}
            style={{
              color: "var(--text-2)",
              textDecoration: "none",
              padding: "10px 0",
            }}
          >
            sign_in()
          </Link>
          <Link
            href="/consultation"
            onClick={() => setMobileMenuOpen(false)}
            style={{
              color: "var(--accent-ink)",
              background: "var(--accent)",
              padding: "12px 16px",
              borderRadius: 4,
              textDecoration: "none",
              fontWeight: 500,
              textAlign: "center",
              marginTop: 8,
            }}
          >
            ./book_consultation.sh
          </Link>
        </div>
      </div>
    </>
  )
}
